import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import type { ScorecardElementSymbol } from '../../../../Scorecard';

export interface PresentationInsight {
  element: ScorecardElementSymbol;
  title: string;
  question: string;
  nextStep: string;
}
interface PresentationInsightsState {
  insights: PresentationInsight[];
  selected?: PresentationInsight;
  open: (element: ScorecardElementSymbol, trigger: HTMLElement) => void;
  close: () => void;
}
const Context = createContext<PresentationInsightsState | null>(null);
export const usePresentationInsights = () => useContext(Context);

export function PresentationInsightsProvider({
  insights,
  children,
}: {
  insights: PresentationInsight[];
  children: ReactNode;
}) {
  const [element, setElement] = useState<ScorecardElementSymbol>();
  const trigger = useRef<HTMLElement>();
  const selected = insights.find((insight) => insight.element === element);
  const close = useCallback(() => {
    setElement(undefined);
    if (trigger.current?.isConnected) trigger.current.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (element && !selected) close();
  }, [element, selected, close]);
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, close]);
  return (
    <Context.Provider
      value={{
        insights,
        selected,
        close,
        open: (next, control) => {
          trigger.current = control;
          setElement(next);
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function PresentationInsightPanel({ onePagePlanPath }: { onePagePlanPath?: string }) {
  const state = usePresentationInsights();
  const { t } = useTranslation('pages');
  const heading = useRef<HTMLHeadingElement>(null);
  const selectedElement = state?.selected?.element;
  useEffect(() => {
    if (selectedElement) heading.current?.focus({ preventScroll: true });
  }, [selectedElement]);
  if (!state?.selected) return null;
  const { selected, close } = state;
  return (
    <section
      id="presentation-insight"
      aria-labelledby="presentation-insight-title"
      data-presentation-control
      className="mt-12 min-h-0 w-full overflow-y-auto rounded-2xl bg-white p-8 text-[#18211f] shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <h2
          id="presentation-insight-title"
          ref={heading}
          tabIndex={-1}
          className="text-[32px] font-bold leading-tight focus:outline-none"
        >
          {selected.title}
        </h2>
        <button
          type="button"
          aria-label={t('full-screen-scorecard-close-insight')}
          className="-mr-3 -mt-3 grid h-12 w-12 shrink-0 place-items-center rounded-full text-[32px] focus-visible:outline focus-visible:outline-2"
          onClick={close}
        >
          ×
        </button>
      </div>
      <p className="mt-8 text-[28px] leading-snug">{selected.question}</p>
      <p className="mt-8 text-[24px] leading-snug text-[#45534f]">{selected.nextStep}</p>
      {onePagePlanPath && (
        <Link
          to={onePagePlanPath}
          className="mt-10 inline-block py-3 text-[22px] font-bold underline underline-offset-4 focus-visible:outline focus-visible:outline-2"
        >
          {t('full-screen-scorecard-insight-one-page-plan')}
        </Link>
      )}
    </section>
  );
}
